import React from 'react'
import { FormProvider } from 'react-hook-form'
import styled from 'styled-components'

import { MFAInstructions } from './components/MFAInstructions'
import { useLogin } from './hooks'

import { AssistanceSection, Layout, Message } from '../../components'
import { TextInput, Button, Spinner } from '../../components/uikit'
import { EMAIL_REGEXP } from '../../constants/email'
import { theme } from '../../theme'

import type { RoutePageProps } from '../taskpane/types'

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const StyledMessage = styled(Message)`
  margin-top: 1rem;
`

const SSOButton = styled(Button)`
  padding: 0;
  font-weight: ${theme.fontWeight.normal};
`

export const LoginApp = ({ onUpdateRoute }: RoutePageProps) => {
  const {
    errors,
    attemptToLogin,
    loading,
    message,
    methods,
    register,
    setMessage,
    isSSOEnabled,
    showMFAInstructions,
  } = useLogin({ onUpdateRoute })

  if (showMFAInstructions) {
    return (
      <MFAInstructions
        emailAddress={methods.watch('email')}
        onClickLink={attemptToLogin}
      />
    )
  }

  return (
    <Layout
      title="Log in"
      subtitle="Review options and send them to employees"
      largeLogo
    >
      <FormProvider {...methods}>
        <FormContainer onSubmit={attemptToLogin}>
          <TextInput
            label="Email"
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: EMAIL_REGEXP,
                message: 'Please enter a valid email address',
              },
            })}
            error={errors?.email}
            disabled={loading}
          />
          {!methods.watch('useSSO') && (
            <TextInput
              label="Password"
              type="password"
              {...register('password', {
                required: 'Password is required',
              })}
              error={errors?.password}
              disabled={loading}
            />
          )}

          {isSSOEnabled && (
            <SSOButton
              mode="link"
              onClick={() =>
                methods.setValue('useSSO', !methods.watch('useSSO'))
              }
            >
              {methods.watch('useSSO')
                ? 'Login with email and password'
                : 'Login with SSO'}
            </SSOButton>
          )}

          {message && (
            <StyledMessage
              type={message.type}
              autoHide={false}
              onClose={() => setMessage(null)}
            >
              {message.text}
            </StyledMessage>
          )}

          <Button
            type="submit"
            isFullWidth
            disabled={loading}
            icon={loading ? <Spinner size={16} /> : <></>}
          >
            Log in
          </Button>
        </FormContainer>
      </FormProvider>

      <AssistanceSection />
    </Layout>
  )
}
